/**
 * Ausência de medição, zero informado e valor medido são estados diferentes.
 * Nada aqui grava no banco nem calcula benefício.
 */

export type KaizenMetricKind = 'duration' | 'percent' | 'fte' | 'money';

export interface KaizenMetricValues {
  baseline_lead_time: number | null;
  target_lead_time: number | null;
  current_lead_time: number | null;
  baseline_cycle_time: number | null;
  target_cycle_time: number | null;
  current_cycle_time: number | null;
  baseline_ftr: number | null;
  target_ftr: number | null;
  current_ftr: number | null;
  baseline_capacity_fte: number | null;
  target_capacity_fte: number | null;
  current_capacity_fte: number | null;
  baseline_cost: number | null;
  target_cost: number | null;
  current_cost: number | null;
}

export function uninformedKaizenMetrics(): KaizenMetricValues {
  return {
    baseline_lead_time: null,
    target_lead_time: null,
    current_lead_time: null,
    baseline_cycle_time: null,
    target_cycle_time: null,
    current_cycle_time: null,
    baseline_ftr: null,
    target_ftr: null,
    current_ftr: null,
    baseline_capacity_fte: null,
    target_capacity_fte: null,
    current_capacity_fte: null,
    baseline_cost: null,
    target_cost: null,
    current_cost: null,
  };
}

/** Campo vazio permanece nulo. "0" e "100" permanecem os números digitados. */
export function parseMetricInput(raw: string): number | null {
  const text = raw.trim().replace(',', '.');
  if (text === '') return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

export function metricValidationError(kind: KaizenMetricKind, value: number | null): string | null {
  if (value == null) return null;
  if (kind === 'percent') {
    if (value < 0 || value > 100) return 'FTR fica entre 0% e 100%.';
    return null;
  }
  if (value < 0) {
    if (kind === 'duration') return 'Tempo não pode ser negativo.';
    if (kind === 'fte') return 'Capacidade não pode ser negativa.';
    return 'Custo não pode ser negativo.';
  }
  return null;
}

/**
 * Espelha detect_regression sem alterá-la.
 * A função SQL só compara lead time quando target_lead_time > 0.
 * NULL > 0 não entra no IF. Zero também não.
 */
export function regressionComparesLeadTime(target: number | null): boolean {
  return typeof target === 'number' && target > 0;
}

export type TargetComparison = 'met' | 'not_met' | 'unavailable';

export interface LeadTimeRealization {
  realizedReductionPercent: number | null;
  targetComparison: TargetComparison;
}

function finiteNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Redução realizada compara baseline com a medição atual.
 * Target permanece meta e não substitui current.
 * Baseline zero não vira ausência e não entra em divisão.
 */
export function realizeLeadTime(
  baseline: number | null | undefined,
  target: number | null | undefined,
  current: number | null | undefined,
): LeadTimeRealization {
  const realizedReductionPercent = finiteNumber(baseline) && finiteNumber(current) && baseline !== 0
    ? ((baseline - current) / baseline) * 100
    : null;
  const targetComparison: TargetComparison = finiteNumber(current) && finiteNumber(target)
    ? (current <= target ? 'met' : 'not_met')
    : 'unavailable';
  return { realizedReductionPercent, targetComparison };
}

/** O trigger só roda depois de um INSERT em metrics_snapshots. */
export function regressionWithoutSnapshot(): false {
  return false;
}

/** current_* nulo não abre medição. Snapshot continua sendo um registro explícito. */
export function currentNullCreatesSnapshot(): false {
  return false;
}

/**
 * Copia só as medições que o snapshot realmente guarda.
 * metrics_snapshots não tem custo. current_cost fica de fora para o valor
 * já gravado no Kaizen permanecer.
 */
export function currentFieldsAfterSnapshot(snapshot: {
  lead_time: number;
  cycle_time?: number | null;
  ftr?: number | null;
  capacity_fte?: number | null;
}): {
  current_lead_time: number;
  current_cycle_time: number | null;
  current_ftr: number | null;
  current_capacity_fte: number | null;
} {
  return {
    current_lead_time: snapshot.lead_time,
    current_cycle_time: snapshot.cycle_time ?? null,
    current_ftr: snapshot.ftr ?? null,
    current_capacity_fte: snapshot.capacity_fte ?? null,
  };
}
